import { useState, useEffect, useRef, useCallback } from 'react';
import { siteKey, creditReferralCommission, updateReferralCounts, getUserByPhone, getAllActivationRequests, updateActivationRequestStatus, getUserByUsername } from '../../../utils/sharedStorage';
import { checkBKaPayStatus, isBKaPayConfigured } from '../../../utils/bkapayService';

// ✅ Notification WhatsApp admin
const notifyAdminNewPayment = async (userName: string, userPhone: string, amount: number, currency: string, paymentMethod: string) => {
  try {
    const adminPhones = ['97800237', '92252664', '90804284', '70636321'];
    const message = `🔔 NOUVEAU PAIEMENT REÇU !\n\n👤 Nom : ${userName}\n📱 Tél : ${userPhone}\n💰 Montant : ${amount.toLocaleString()} ${currency}\n💳 Méthode : ${paymentMethod}\n⏰ ${new Date().toLocaleString('fr-FR')}\n\n➡️ Connectez-vous au panel admin pour valider.`;
    const encodedMsg = encodeURIComponent(message);
    for (const phone of adminPhones) {
      const apiKey = localStorage.getItem(`callmebot_key_${phone}`) || '1234567';
      await fetch(`https://api.callmebot.com/whatsapp.php?phone=${phone}&text=${encodedMsg}&apikey=${apiKey}`, { method: 'GET', mode: 'no-cors' });
    }
  } catch { /* silencieux */ }
};

const notifyUserActivated = async (userPhone: string, userName: string) => {
  try {
    const cleanPhone = userPhone.replace(/[\s+\-()]/g, '');
    const message = `✅ Bonjour ${userName} !\n\nVotre compte SUCCESSLINK AGENCY a été activé avec succès. 🎉\n\nVous pouvez maintenant vous connecter et accéder à votre tableau de bord.\n\nBienvenue dans la famille SUCCESSLINK ! 💪`;
    const encodedMsg = encodeURIComponent(message);
    const apiKey = localStorage.getItem(`callmebot_key_${cleanPhone}`) || '1234567';
    await fetch(`https://api.callmebot.com/whatsapp.php?phone=${cleanPhone}&text=${encodedMsg}&apikey=${apiKey}`, { method: 'GET', mode: 'no-cors' });
  } catch { /* silencieux */ }
};

const notifyUserRejected = async (userPhone: string, userName: string, reason: string) => {
  try {
    const cleanPhone = userPhone.replace(/[\s+\-()]/g, '');
    const message = `❌ Bonjour ${userName},\n\nVotre demande d'activation a été refusée.\n\n📋 Motif : ${reason}\n\nVeuillez soumettre un nouveau paiement valide ou contacter le support.\n\n— SUCCESSLINK AGENCY`;
    const encodedMsg = encodeURIComponent(message);
    const apiKey = localStorage.getItem(`callmebot_key_${cleanPhone}`) || '1234567';
    await fetch(`https://api.callmebot.com/whatsapp.php?phone=${cleanPhone}&text=${encodedMsg}&apikey=${apiKey}`, { method: 'GET', mode: 'no-cors' });
  } catch { /* silencieux */ }
};

interface ActivationRequest {
  id: string;
  userPhone: string;
  userName: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  proof: string;
  paymentPhone?: string;
  screenshotProof?: string;
  status: 'pending' | 'approved' | 'rejected' | 'validated';
  date: string;
  country: string;
  rejectionReason?: string;
  transactionId?: string;
  autoValidated?: boolean;
  validatedAt?: string;
}

// ── Son de notification ──────────────────────────────────────────────────────
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    oscillator.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.5);
  } catch { /* ignore */ }
}

export default function DepositValidation() {
  const [requests, setRequests] = useState<ActivationRequest[]>([]);
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'validated' | 'all'>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const isFetchingRef = useRef(false);
  const knownIdsRef = useRef<Set<string>>(new Set());
  const isFirstLoadRef = useRef(true);
  const [newPaymentAlert, setNewPaymentAlert] = useState<ActivationRequest | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const [rejectModal, setRejectModal] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');
  const [deletePendingModal, setDeletePendingModal] = useState(false);
  const [isDeletingPending, setIsDeletingPending] = useState(false);
  const [verifyingBKaPay, setVerifyingBKaPay] = useState<string | null>(null);

  // ── Charger depuis localStorage ───────────────────────────────────────────
  const loadRequests = useCallback(async (silent = false) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const allRequests = await getAllActivationRequests();
      allRequests.sort((a: ActivationRequest, b: ActivationRequest) => {
        try { return new Date(b.date).getTime() - new Date(a.date).getTime(); } catch { return 0; }
      });

      // ── Détecter les nouvelles demandes en attente ──
      if (!isFirstLoadRef.current) {
        const newPending = allRequests.filter(
          r => r.status === 'pending' && !knownIdsRef.current.has(r.id)
        );
        if (newPending.length > 0) {
          // Jouer le son et afficher l'alerte pour la plus récente
          playNotificationSound();
          setNewPaymentAlert(newPending[0]);
          setTimeout(() => setNewPaymentAlert(null), 8000);
        }
      }

      // Mettre à jour les IDs connus
      allRequests.forEach(r => knownIdsRef.current.add(r.id));
      isFirstLoadRef.current = false;

      setRequests(allRequests);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Erreur chargement demandes:', error);
      try {
        const localRaw = localStorage.getItem(siteKey('activationRequests')) || localStorage.getItem('activationRequests') || '[]';
        const localRequests: ActivationRequest[] = JSON.parse(localRaw);
        setRequests(localRequests);
      } catch { /* ignore */ }
    } finally {
      isFetchingRef.current = false;
      if (!silent) setIsLoading(false);
    }
  }, []);

  // ── Polling toutes les 2 secondes ─────────────────────────────────────────
  useEffect(() => {
    loadRequests();
    const interval = setInterval(() => {
      loadRequests(true);
    }, 2000);
    return () => clearInterval(interval);
  }, [loadRequests]);

  // ── Écouter BroadcastChannel pour les nouvelles soumissions ──────────────
  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('new_payment_submitted');
      channel.onmessage = () => {
        loadRequests(true);
      };
    } catch { /* ignore */ }
    return () => { try { channel?.close(); } catch { /* ignore */ } };
  }, [loadRequests]);

  const safeParseJSON = <T,>(raw: string | null, fallback: T): T => {
    if (!raw) return fallback;
    try { return JSON.parse(raw) as T; } catch { return fallback; }
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  // ── Activer le compte utilisateur ─────────────────────────────────────────
  const activateUserAccount = (userPhone: string) => {
    try {
      const userKey = siteKey(`user_${userPhone}`);
      const userLocal = safeParseJSON<any>(localStorage.getItem(userKey) || localStorage.getItem(`user_${userPhone}`), {});
      userLocal.accountStatus = 'active';
      userLocal.isActivated = true;
      userLocal.activated = true;
      userLocal.activatedAt = new Date().toISOString();
      localStorage.setItem(userKey, JSON.stringify(userLocal));
      localStorage.setItem(`user_${userPhone}`, JSON.stringify(userLocal));

      const currentUser = safeParseJSON<any>(localStorage.getItem('currentUser'), {});
      if (currentUser.phone === userPhone) {
        currentUser.accountStatus = 'active';
        currentUser.isActivated = true;
        currentUser.activated = true;
        currentUser.activatedAt = new Date().toISOString();
        localStorage.setItem('currentUser', JSON.stringify(currentUser));
      }

      const activationSignal = { activated: true, activatedAt: new Date().toISOString(), phone: userPhone, ts: Date.now() };
      const signalStr = JSON.stringify(activationSignal);
      localStorage.setItem(`activation_signal_${userPhone}`, signalStr);
      localStorage.setItem(siteKey(`activation_signal_${userPhone}`), signalStr);
      localStorage.setItem('lastActivation', signalStr);
      localStorage.setItem('lastActivationPhone', userPhone);

      try {
        const channel = new BroadcastChannel('account_activation');
        channel.postMessage({ type: 'ACCOUNT_ACTIVATED', phone: userPhone, activatedAt: new Date().toISOString() });
        channel.close();
      } catch { /* ignore */ }

      try {
        const channel2 = new BroadcastChannel('balance_update');
        channel2.postMessage({ type: 'ACCOUNT_ACTIVATED', phone: userPhone, ts: Date.now() });
        channel2.close();
      } catch { /* ignore */ }

    } catch { /* ignore */ }
  };

  // ── Valider un paiement ───────────────────────────────────────────────────
  const handleValidate = async (id: string) => {
    const req = requests.find(r => r.id === id);
    if (!req) return;
    setIsLoading(true);

    try {
      await updateActivationRequestStatus(id, 'validated' as any);
      activateUserAccount(req.userPhone);
      await creditReferralCommissionsLocal(req.userPhone, req.userName);
      notifyUserActivated(req.userPhone, req.userName);
      showSuccess(`✅ Compte de ${req.userName} activé ! Commissions créditées automatiquement.`);
      await loadRequests();
    } catch (err) {
      console.error('Erreur validation:', err);
      showSuccess('⚠️ Erreur lors de la validation. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Commissions de parrainage ─────────────────────────────────────────────
  const creditReferralCommissionsLocal = async (activatedPhone: string, activatedName: string) => {
    try {
      const activatedUser = await getUserByPhone(activatedPhone);
      if (!activatedUser?.referredBy) return;

      const level1Username = activatedUser.referredBy;
      await creditReferralCommission(level1Username, activatedName, 1, 1500);
      await updateReferralCounts(level1Username);

      const level1User = await getUserByUsername(level1Username);
      if (level1User?.referredBy) {
        const level2Username = level1User.referredBy;
        await creditReferralCommission(level2Username, activatedName, 2, 600);
        await updateReferralCounts(level2Username);

        const level2User = await getUserByUsername(level2Username);
        if (level2User?.referredBy) {
          const level3Username = level2User.referredBy;
          await creditReferralCommission(level3Username, activatedName, 3, 400);
          await updateReferralCounts(level3Username);
        }
      }
    } catch (e) {
      console.error('Erreur commissions:', e);
    }
  };

  // ── Vérification manuelle BKaPay ──────────────────────────────────────────
  const handleVerifyBKaPay = async (id: string) => {
    const req = requests.find(r => r.id === id);
    if (!req || !req.proof) { showSuccess('⚠️ Aucun ID de transaction BKaPay trouvé'); return; }
    setVerifyingBKaPay(id);
    try {
      const result = await checkBKaPayStatus(req.proof);
      if (!result.success) { showSuccess(`❌ Erreur de vérification : ${result.error}`); return; }
      if (result.status === 'PAID' || result.status === 'completed') {
        const allLocal: ActivationRequest[] = safeParseJSON<ActivationRequest[]>(localStorage.getItem(siteKey('activationRequests')), []);
        const updatedLocal = allLocal.map(r =>
          r.id === id ? { ...r, status: 'validated' as const, autoValidated: true, validatedAt: new Date().toISOString() } : r
        );
        localStorage.setItem(siteKey('activationRequests'), JSON.stringify(updatedLocal));
        localStorage.setItem('activationRequests', JSON.stringify(updatedLocal));
        activateUserAccount(req.userPhone);
        await creditReferralCommissionsLocal(req.userPhone, req.userName);
        showSuccess(`✅ Paiement BKaPay confirmé ! Compte de ${req.userName} activé.`);
        await loadRequests();
      } else if (result.status === 'PENDING' || result.status === 'pending') {
        showSuccess('⏳ Paiement en attente de confirmation sur BKaPay');
      } else if (result.status === 'FAILED' || result.status === 'failed') {
        showSuccess('❌ Le paiement BKaPay a échoué');
      } else if (result.status === 'EXPIRED') {
        showSuccess('⏰ Le paiement BKaPay a expiré');
      } else {
        showSuccess(`ℹ️ Statut BKaPay : ${result.status || 'Inconnu'}`);
      }
    } catch (error) {
      console.error('Erreur vérification BKaPay:', error);
      showSuccess('⚠️ Erreur lors de la vérification BKaPay');
    } finally {
      setVerifyingBKaPay(null);
    }
  };

  const openRejectModal = (id: string, name: string) => {
    setRejectModal({ open: true, id, name });
    setRejectReason('');
    setRejectError('');
  };

  // ── Rejeter un paiement ───────────────────────────────────────────────────
  const confirmReject = async () => {
    if (!rejectReason.trim()) { setRejectError('Veuillez indiquer la raison du rejet.'); return; }
    const req = requests.find(r => r.id === rejectModal.id);
    if (!req) return;
    setIsLoading(true);
    try {
      await updateActivationRequestStatus(rejectModal.id, 'rejected', rejectReason.trim());
      try {
        const userKey = siteKey(`user_${req.userPhone}`);
        const userLocal = safeParseJSON<any>(localStorage.getItem(userKey) || localStorage.getItem(`user_${req.userPhone}`), {});
        userLocal.accountStatus = 'rejected';
        userLocal.isActivated = false;
        userLocal.rejectionReason = rejectReason.trim();
        localStorage.setItem(userKey, JSON.stringify(userLocal));
        localStorage.setItem(`user_${req.userPhone}`, JSON.stringify(userLocal));
        const currentUser = safeParseJSON<any>(localStorage.getItem('currentUser'), {});
        if (currentUser.phone === req.userPhone) {
          currentUser.accountStatus = 'rejected';
          currentUser.isActivated = false;
          currentUser.rejectionReason = rejectReason.trim();
          localStorage.setItem('currentUser', JSON.stringify(currentUser));
        }
      } catch { /* ignore */ }
      notifyUserRejected(req.userPhone, req.userName, rejectReason.trim());
      setRejectModal({ open: false, id: '', name: '' });
      await loadRequests();
      showSuccess(`❌ Demande de ${req.userName} rejetée. Notification envoyée.`);
    } catch {
      showSuccess('⚠️ Erreur lors du rejet. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Supprimer toutes les demandes en attente ──────────────────────────────
  const handleDeleteAllPending = async () => {
    setIsDeletingPending(true);
    try {
      const pendingIds = requests.filter(r => r.status === 'pending').map(r => r.id);
      const allLocal: ActivationRequest[] = safeParseJSON<ActivationRequest[]>(localStorage.getItem(siteKey('activationRequests')), []);
      const updatedLocal = allLocal.filter(r => r.status !== 'pending');
      localStorage.setItem(siteKey('activationRequests'), JSON.stringify(updatedLocal));
      localStorage.setItem('activationRequests', JSON.stringify(updatedLocal));
      setRequests(prev => prev.filter(r => r.status !== 'pending'));
      showSuccess(`✅ ${pendingIds.length} demande(s) en attente supprimée(s) !`);
      setDeletePendingModal(false);
    } catch (error) {
      console.error('Erreur suppression:', error);
      showSuccess('⚠️ Erreur lors de la suppression.');
    } finally {
      setIsDeletingPending(false);
    }
  };

  const filtered = requests.filter(r => {
    const matchStatus = filter === 'all' || r.status === filter || (filter === 'approved' && r.status === 'validated');
    const q = searchQuery.trim().toLowerCase();
    if (!q) return matchStatus;
    const matchSearch =
      (r.userPhone ?? '').toLowerCase().includes(q) ||
      (r.paymentPhone ?? '').toLowerCase().includes(q) ||
      (r.proof ?? '').toLowerCase().includes(q) ||
      (r.userName ?? '').toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const formatDate = (dateString: string) => {
    try { return new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return dateString; }
  };

  const isRecent = (dateString: string) => {
    try { return Date.now() - new Date(dateString).getTime() < 5 * 60 * 1000; } catch { return false; }
  };

  const isVeryRecent = (dateString: string) => {
    try { return Date.now() - new Date(dateString).getTime() < 60 * 1000; } catch { return false; }
  };

  const statusBadge = (status: string) => {
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
    if (status === 'approved' || status === 'validated') return 'bg-green-100 text-green-800';
    return 'bg-red-100 text-red-800';
  };

  const statusLabel = (status: string) => {
    if (status === 'pending') return 'En attente';
    if (status === 'approved' || status === 'validated') return 'Validé ✅';
    return 'Rejeté ❌';
  };

  const methodIcon: Record<string, string> = {
    'BKaPay': 'ri-bank-card-line', 'Soleaspay': 'ri-secure-payment-line',
    'Money Fusion': 'ri-exchange-funds-line', 'USDT BEP20': 'ri-coin-line',
    'Mobile Money Ghana': 'ri-smartphone-line',
  };

  const highlight = (text: string) => {
    if (!searchQuery.trim() || !text) return text;
    const q = searchQuery.trim();
    try {
      const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = text.split(regex);
      return parts.map((part, i) =>
        regex.test(part) ? <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">{part}</mark> : part
      );
    } catch { return text; }
  };

  return (
    <div>
      {isLoading && (
        <div className="fixed top-4 right-4 z-50 bg-emerald-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i>
          <span className="text-sm font-semibold">Chargement...</span>
        </div>
      )}

      {/* ── Alerte nouveau paiement ── */}
      {newPaymentAlert && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white rounded-2xl shadow-2xl p-4 flex items-center gap-4 animate-bounce border-2 border-white/30">
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
              <i className="ri-notification-3-fill text-2xl"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-extrabold text-base leading-tight">🔔 NOUVEAU PAIEMENT !</p>
              <p className="text-sm text-white/90 truncate font-semibold">{newPaymentAlert.userName}</p>
              <p className="text-xs text-white/70">{newPaymentAlert.amount.toLocaleString()} {newPaymentAlert.currency} · {newPaymentAlert.paymentMethod}</p>
            </div>
            <button
              onClick={() => { setNewPaymentAlert(null); setFilter('pending'); }}
              className="px-3 py-2 bg-white text-red-600 font-bold rounded-xl text-xs cursor-pointer whitespace-nowrap hover:bg-red-50 transition"
            >
              Voir
            </button>
            <button onClick={() => setNewPaymentAlert(null)} className="w-7 h-7 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full cursor-pointer transition flex-shrink-0">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Modal de rejet */}
      {rejectModal.open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-close-circle-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Rejeter le paiement</h3>
                <p className="text-sm text-gray-500">Demande de <strong>{rejectModal.name}</strong></p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Raison du rejet <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => { setRejectReason(e.target.value); setRejectError(''); }}
                rows={4}
                maxLength={500}
                placeholder="Ex: La capture d'écran est illisible..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent outline-none resize-none"
              />
              <div className="flex items-center justify-between mt-1">
                {rejectError ? (
                  <p className="text-xs text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i> {rejectError}</p>
                ) : <span />}
                <span className="text-xs text-gray-400 ml-auto">{rejectReason.length}/500</span>
              </div>
            </div>
            <div className="mb-5">
              <p className="text-xs text-gray-500 font-semibold mb-2">Raisons fréquentes :</p>
              <div className="flex flex-wrap gap-2">
                {['Capture d\'écran illisible', 'Montant incorrect', 'Paiement non reçu', 'Référence introuvable'].map(suggestion => (
                  <button key={suggestion} type="button" onClick={() => setRejectReason(suggestion)}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-red-50 hover:text-red-700 text-gray-600 text-xs rounded-full border border-gray-200 hover:border-red-300 transition cursor-pointer whitespace-nowrap">
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setRejectModal({ open: false, id: '', name: '' })} disabled={isLoading}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50">
                Annuler
              </button>
              <button onClick={confirmReject} disabled={isLoading}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50">
                {isLoading ? <><i className="ri-loader-4-line animate-spin text-lg"></i>Envoi...</> : <><i className="ri-close-circle-line text-lg"></i>Confirmer le rejet</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal suppression en masse */}
      {deletePendingModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-alarm-warning-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Supprimer toutes les demandes en attente</h3>
                <p className="text-sm text-gray-500">Action irréversible</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-800 font-semibold">
                ⚠️ Vous allez supprimer <strong>{pendingCount}</strong> demande{pendingCount > 1 ? 's' : ''} en attente !
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeletePendingModal(false)} disabled={isDeletingPending}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50">Annuler</button>
              <button onClick={handleDeleteAllPending} disabled={isDeletingPending}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50">
                {isDeletingPending ? <><i className="ri-loader-4-line animate-spin text-lg"></i>Suppression...</> : <><i className="ri-delete-bin-line text-lg"></i>Tout supprimer</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* En-tête */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-bold text-gray-900">Validation des paiements d'activation</h2>
            {/* Indicateur temps réel */}
            <span className="flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold border border-emerald-200">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              EN DIRECT · màj {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => loadRequests()} disabled={isLoading}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50">
              <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
              Actualiser
            </button>
            {pendingCount > 0 && (
              <>
                <button onClick={() => setDeletePendingModal(true)} disabled={isLoading}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50">
                  <i className="ri-delete-bin-line"></i>
                  Supprimer en attente ({pendingCount})
                </button>
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-bold animate-pulse">
                  <i className="ri-alarm-warning-line"></i>
                  {pendingCount} en attente
                </span>
              </>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500">
          Total : <strong>{requests.length}</strong> demande{requests.length > 1 ? 's' : ''} — Rafraîchissement automatique toutes les 2 secondes.
        </p>
      </div>

      {successMsg && (
        <div className="mb-4 px-4 py-3 bg-green-100 text-green-700 rounded-lg flex items-center gap-2 font-semibold text-sm">
          <i className="ri-checkbox-circle-fill text-lg"></i> {successMsg}
        </div>
      )}

      {/* Barre de recherche */}
      <div className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i className="ri-search-line text-gray-400 text-lg"></i>
          </div>
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher par téléphone, référence ou nom..."
            className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent outline-none bg-white shadow-sm" />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400 hover:text-gray-600">
              <i className="ri-close-circle-fill text-lg"></i>
            </button>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 mb-6">
        {([
          { key: 'pending', label: 'En attente', count: requests.filter(r => r.status === 'pending').length },
          { key: 'approved', label: 'Validés', count: requests.filter(r => r.status === 'approved' || r.status === 'validated').length },
          { key: 'rejected', label: 'Rejetés', count: requests.filter(r => r.status === 'rejected').length },
          { key: 'all', label: 'Tous', count: requests.length },
        ] as const).map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap ${
              filter === f.key ? 'bg-red-600 text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {f.label}
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${filter === f.key ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>
              {f.count}
            </span>
            {f.key === 'pending' && f.count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse font-bold shadow">
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
            <i className={`${searchQuery ? 'ri-search-line' : 'ri-inbox-line'} text-4xl text-gray-300`}></i>
          </div>
          <p className="text-gray-500 font-medium">
            {searchQuery ? `Aucun résultat pour "${searchQuery}"` : 'Aucune demande à afficher'}
          </p>
          <p className="text-gray-400 text-sm mt-1">
            {filter === 'pending' ? 'Aucun paiement en attente de validation.' : 'Aucune demande dans cette catégorie.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(req => (
            <div key={req.id}
              className={`bg-white rounded-xl shadow border-l-4 p-5 transition hover:shadow-md ${
                req.status === 'pending'
                  ? isVeryRecent(req.date) ? 'border-red-500 ring-2 ring-red-200' : 'border-yellow-400'
                  : (req.status === 'approved' || req.status === 'validated') ? 'border-green-500' : 'border-red-400'
              }`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0">
                      <i className={`${methodIcon[req.paymentMethod] || 'ri-money-dollar-circle-line'} text-xl text-gray-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-base">{highlight(req.userName)}</h3>
                      <p className="text-xs text-gray-500">{highlight(req.userPhone)} — {req.country}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-2 flex-wrap">
                      {req.status === 'pending' && isVeryRecent(req.date) && (
                        <span className="flex items-center gap-1 px-2.5 py-1 bg-red-600 text-white text-xs font-extrabold rounded-full animate-pulse shadow">
                          <i className="ri-flashlight-fill"></i>INSTANT
                        </span>
                      )}
                      {req.status === 'pending' && isRecent(req.date) && !isVeryRecent(req.date) && (
                        <span className="flex items-center gap-1 px-2.5 py-1 bg-red-500 text-white text-xs font-extrabold rounded-full animate-pulse shadow">
                          <i className="ri-alarm-warning-line"></i>NOUVEAU
                        </span>
                      )}
                      {req.autoValidated && (req.status === 'approved' || req.status === 'validated') && (
                        <span className="flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full border border-blue-300">
                          <i className="ri-shield-check-fill"></i>Vérifié BKaPay
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusBadge(req.status)}`}>
                        {statusLabel(req.status)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                    <div className="bg-gray-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-gray-400 font-medium">Montant</p>
                      <p className="font-bold text-red-600 text-base">{req.amount.toLocaleString()} {req.currency}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-gray-400 font-medium">Méthode</p>
                      <p className="font-semibold text-gray-800 flex items-center gap-1">
                        <i className={`${methodIcon[req.paymentMethod] || 'ri-money-dollar-circle-line'} text-sm`}></i>
                        {req.paymentMethod}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg px-3 py-2 col-span-2">
                      <p className="text-xs text-gray-400 font-medium">Date de soumission</p>
                      <p className="text-sm text-gray-700">{formatDate(req.date)}</p>
                    </div>
                  </div>

                  {req.proof && (
                    <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl px-4 py-3 mb-3">
                      <p className="text-xs text-emerald-700 font-bold mb-1.5 flex items-center gap-1.5">
                        <i className="ri-hashtag text-base"></i>
                        Référence de transaction
                        <span className="ml-auto px-2 py-0.5 bg-emerald-200 text-emerald-800 rounded-full text-xs font-bold">À vérifier</span>
                      </p>
                      <p className="font-mono text-sm text-emerald-900 font-bold bg-white border border-emerald-200 rounded-lg px-3 py-2 break-all">
                        {highlight(req.proof)}
                      </p>
                    </div>
                  )}

                  {req.paymentPhone && (
                    <div className="bg-indigo-50 border-2 border-indigo-200 rounded-xl px-4 py-3 mb-3">
                      <p className="text-xs text-indigo-700 font-bold mb-1.5 flex items-center gap-1.5">
                        <i className="ri-smartphone-line text-base"></i>
                        Numéro utilisé pour le paiement
                      </p>
                      <p className="text-sm text-indigo-900 font-bold bg-white border border-indigo-200 rounded-lg px-3 py-2 flex items-center gap-2">
                        <i className="ri-phone-line text-indigo-500"></i>
                        {highlight(req.paymentPhone)}
                      </p>
                    </div>
                  )}

                  {req.screenshotProof && (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-3">
                      <p className="text-xs font-bold text-blue-800 mb-3 flex items-center gap-2">
                        <i className="ri-image-line text-lg"></i>
                        Capture d'écran
                      </p>
                      <div className="relative">
                        <img src={req.screenshotProof} alt="Preuve de paiement"
                          className="w-full max-h-64 object-contain rounded-lg border-2 border-gray-200 bg-white cursor-pointer hover:opacity-90 transition"
                          onClick={() => setSelectedImage(req.screenshotProof || '')} />
                        <button onClick={() => setSelectedImage(req.screenshotProof || '')}
                          className="mt-2 w-full py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 transition">
                          <i className="ri-zoom-in-line"></i>Voir en grand
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {req.status === 'pending' && (
                <div className="flex gap-3 mt-2 pt-3 border-t border-gray-100">
                  {req.paymentMethod === 'BKaPay' && req.proof && isBKaPayConfigured() && (
                    <button onClick={() => handleVerifyBKaPay(req.id)} disabled={verifyingBKaPay === req.id || isLoading}
                      className="flex-1 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 border border-blue-200 disabled:opacity-50">
                      {verifyingBKaPay === req.id ? <><i className="ri-loader-4-line animate-spin text-lg"></i>Vérification...</> : <><i className="ri-shield-check-line text-lg"></i>Vérifier via API</>}
                    </button>
                  )}
                  <button onClick={() => openRejectModal(req.id, req.userName)} disabled={isLoading}
                    className="flex-1 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 border border-red-200 disabled:opacity-50">
                    <i className="ri-close-circle-line text-lg"></i>Rejeter
                  </button>
                  <button onClick={() => handleValidate(req.id)} disabled={isLoading}
                    className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    Valider &amp; Activer
                  </button>
                </div>
              )}

              {(req.status === 'approved' || req.status === 'validated') && (
                <div className="mt-2 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-green-700 font-semibold bg-green-50 rounded-lg px-3 py-2">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                    {req.autoValidated ? 'Validé automatiquement via BKaPay ✅' : 'Paiement validé — Compte activé ✅'}
                  </div>
                  {req.validatedAt && (
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                      <i className="ri-time-line"></i>Validé le {formatDate(req.validatedAt)}
                    </p>
                  )}
                </div>
              )}

              {req.status === 'rejected' && (
                <div className="mt-2 pt-3 border-t border-gray-100 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-red-600 font-semibold bg-red-50 rounded-lg px-3 py-2">
                    <i className="ri-close-circle-fill text-lg"></i>Paiement rejeté
                  </div>
                  {req.rejectionReason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <p className="text-xs text-red-500 font-semibold mb-1">Message envoyé à l'utilisateur :</p>
                      <p className="text-sm text-red-800 italic">"{req.rejectionReason}"</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal image agrandie */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage('')}>
          <div className="relative max-w-5xl w-full">
            <button onClick={() => setSelectedImage('')} className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full cursor-pointer transition">
              <i className="ri-close-line text-2xl"></i>
            </button>
            <img src={selectedImage} alt="Preuve de paiement agrandie" className="w-full h-auto max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          </div>
        </div>
      )}
    </div>
  );
}
